import React, { useId, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LineItemsList, { type LineColumn } from '../ui/LineItemsList';
import StatusBadge from '../ui/StatusBadge';
import { useMoney } from '../../hooks/useMoney';
import { editProjectPath } from '../../utils/accountRoutes';
import { formatDate } from '../../utils/formatDate';
import { CURRENCY_NOT_SET } from '../../utils/currency';
import { PIPELINE_STAGE_LABELS, PIPELINE_STAGE_VARIANT } from '../../utils/pipelineStages';
import type { Workspace } from '../../utils/workspaces';
import { lineForProject, newDraftLine, pickableProjects, pickerLabel, projectRef, type ClientProjectLine, type ProjectOwner } from './clientProjectLines';
import '../../styles/components/ClientProjects.css';

/** What the "which projects are free" lookup is doing; Add waits for it. */
export type OwnersState = 'loading' | 'ready' | 'error';

interface ClientProjectsSectionProps {
  clientId: string;
  lines: readonly ClientProjectLine[];
  onChange: (lines: ClientProjectLine[]) => void;
  /** The person's project list, for the picker. */
  workspaces: readonly Workspace[];
  owners: ReadonlyMap<string, ProjectOwner>;
  ownersState: OwnersState;
  errors: Readonly<Record<string, string>>;
  /** Show the contract value column (not for blind roles). */
  showMoney: boolean;
  disabled: boolean;
}

const dateText = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : formatDate(d);
};

/** This client's projects, one line each, linked or unlinked with the page's Save. */
const ClientProjectsSection: React.FC<ClientProjectsSectionProps> = ({
  clientId, lines, onChange, workspaces, owners, ownersState, errors, showMoney, disabled,
}) => {
  const uid = useId();
  const money = useMoney();
  const patch = (key: string, next: ClientProjectLine) => onChange(lines.map(l => (l.key === key ? next : l)));
  const free = useMemo(() => pickableProjects(workspaces, lines, owners), [workspaces, lines, owners]);

  const columns: LineColumn<ClientProjectLine>[] = [
    {
      key: 'project',
      header: 'Project',
      render: (line, ctx) => line.projectId ? (
        <div className="cpl-project">
          <Link className="cpl-project__name" to={editProjectPath(line.projectId)}>{line.name || projectRef(line.projectId)}</Link>
          <span className="cpl-project__ref">{projectRef(line.projectId)}</span>
          {line.movedFrom && <span className="cpl-project__note" role="status">Now with {line.movedFrom}. Saving moves it to this client.</span>}
        </div>
      ) : (
        <select
          value=""
          aria-label={ctx.ariaLabel}
          aria-describedby={ctx.errorId}
          disabled={ctx.disabled}
          onChange={e => {
            const chosen = workspaces.find(w => w.project_id === e.target.value);
            if (chosen) patch(line.key, lineForProject(line.key, chosen, owners.get(chosen.project_id), clientId));
          }}
        >
          <option value="">Choose a project</option>
          {pickableProjects(workspaces, lines, owners, line.key).map(w => (
            <option key={w.project_id} value={w.project_id}>{pickerLabel(w, owners.get(w.project_id), clientId)}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'date',
      header: 'Event date',
      render: line => <span className="cpl-text">{dateText(line.date) ?? '—'}</span>,
    },
    {
      key: 'stage',
      header: 'Stage',
      render: line => line.stage
        ? <StatusBadge variant={PIPELINE_STAGE_VARIANT[line.stage]}>{PIPELINE_STAGE_LABELS[line.stage]}</StatusBadge>
        : <span className="cpl-text">—</span>,
    },
    ...(showMoney ? [{
      key: 'value',
      header: 'Contract value',
      render: (line: ClientProjectLine) => {
        if (line.contractValue === null) return <span className="cpl-text">—</span>;
        return <span className="cpl-text cpl-text--money">{line.contractCurrency ? money.format(Number(line.contractValue), line.contractCurrency) : CURRENCY_NOT_SET}</span>;
      },
    } as LineColumn<ClientProjectLine>] : []),
  ];

  const addReason = ownersState === 'loading' ? 'Checking which projects are free…'
    : ownersState === 'error' ? "Couldn't check which projects belong to other clients, so adding is off. Reload the page to try again."
      : workspaces.length === 0 ? 'This workspace has no projects yet.'
        : free.length === 0 ? 'Every project you can edit is already listed.' : null;
  // A blank line is already waiting for a choice.
  const hasBlank = lines.some(l => !l.projectId);

  return (
    <section className="settings-card ep-section" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="ap-section-title">Projects</h2>
      <p className="settings-muted-xs">
        The projects for this client. Changes are saved with the page. Taking a project off only unlinks it; the project itself stays.
      </p>
      <div className="cpl-scroll">
        <LineItemsList<ClientProjectLine>
          label="Projects for this client"
          lines={lines}
          getKey={l => l.key}
          lineName={l => l.name}
          columns={columns}
          errors={errors}
          disabled={disabled}
          addLabel="Add project"
          addDisabledReason={addReason ?? (hasBlank ? 'Choose a project on the empty line first.' : null)}
          onAdd={() => onChange([...lines, newDraftLine()])}
          onRemove={line => onChange(lines.filter(l => l.key !== line.key))}
          emptyTitle="No projects for this client yet."
          emptyHint={disabled ? undefined : 'Use Add project to link one.'}
        />
      </div>
    </section>
  );
};

export default ClientProjectsSection;
